import {
  BILLING_CYCLE,
  DISCOUNT_TIER,
} from "api/interfaces/subscription.interface";
import {
  AddPaymentMethodRequest,
  GetPlanServicesResponse,
  PaymentPrice,
} from "api/interfaces/planInterface.interface";
import { useAuth } from "components/Auth";
import { StepType } from "components/blocks/stepper/Stepper";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { getPlanServices, postPlanAddPayment } from "api/planAPI";
import useStatusMutation from "hook/useStatusMutation";
import { AddPaymentMethodResponse } from "api/interfaces/planInterface.interface";
import {
  getYearDiscount,
  getYearlyChannelPrice,
  isYearlyBillingCycle,
} from "utils/priceUtils";

export interface UsageSignupFeature {
  key: string;
  label: string;
}

export type UsageSignupStepKey = "channels" | "cloudStorage" | "reviewPurchase";

interface UsageSignupStepConfig extends StepType {
  key: UsageSignupStepKey;
}

export interface UsageSignupState {
  currentStepKey: UsageSignupStepKey;
  currentStep: number;
  steps: StepType[];
  isUpgrade: boolean;
  paymentData: AddPaymentMethodRequest;
  paymentPrice: PaymentPrice;
  termYears: number;
  isLoading: boolean;
  noPermission: boolean;
}

export interface UsageSignupActions {
  onChangeBillingCycle: (cycle: BILLING_CYCLE) => void;
  onChangeChannels: (value: number | null) => void;
  onChangeStorage: (value: number | null) => void;
  onChangeBillingEmail: (value: string) => void;
  onChangeTermYears: (years: number) => void;
  onChangePage: (name: string, index?: number) => void;
  onAddPayment: () => void;
}

export interface UsageSignupFlow {
  state: UsageSignupState;
  actions: UsageSignupActions;
}

export const usageSignupSteps: UsageSignupStepConfig[] = [
  { key: "channels", label: "Channels" },
  { key: "cloudStorage", label: "Cloud Storage" },
  { key: "reviewPurchase", label: "Review Purchase" },
];

export const includedFeatures: UsageSignupFeature[] = [
  { key: "health-alerts", label: "Health Alerts" },
  { key: "reports", label: "Reports" },
  { key: "video-sharing", label: "Video Sharing" },
  { key: "cvv", label: "CVV (Camera View Verification)" },
];

const initialPlanPrices: PaymentPrice = {
  channelPrice: 0,
  storagePrice: 0,
  year: [],
  registerCamera: 1,
  tierName: DISCOUNT_TIER.CUSTOM,
  tierDiscount: 0,
};

const clampMin = (value: number, min: number): number => Math.max(value, min);

export const TERM_YEAR_OPTIONS = [1, 2, 3] as const;
const DEFAULT_TERM_YEARS = TERM_YEAR_OPTIONS[0];

type PaymentDataBase = {
  userEmail: string;
  channels: number;
  storage: number;
  isUpgrade?: boolean;
};

const buildPaymentDataForCycle = (
  cycle: BILLING_CYCLE,
  base: PaymentDataBase,
  termYears: number,
  prices?: { channelPrice: number; storagePrice: number }
): AddPaymentMethodRequest => {
  if (isYearlyBillingCycle(cycle)) {
    return {
      ...base,
      cycle,
      storage: 0,
      channelPrice: prices?.channelPrice ?? 0,
      storagePrice: prices?.storagePrice ?? 0,
      termYears,
    };
  }
  return {
    ...base,
    cycle,
  };
};

const toPaymentBase = (data: AddPaymentMethodRequest): PaymentDataBase => {
  const { userEmail, channels, storage, isUpgrade } = data;
  return { userEmail, channels, storage, isUpgrade };
};

export default function useUsageSignupFlow({
  cycle = BILLING_CYCLE.MONTHLY,
  isUpgrade = false,
}: {
  cycle?: BILLING_CYCLE;
  isUpgrade?: boolean;
}): UsageSignupFlow {
  const { user } = useAuth();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const noPermission = selectedAccount.isHidden || !selectedAccount.userPermission.manageBillingAndLicenses;

  const [hasEditedChannels, setHasEditedChannels] = useState<boolean>(false);
  const [termYears, setTermYears] = useState<number>(DEFAULT_TERM_YEARS);
  const [paymentData, setPaymentData] = useState<AddPaymentMethodRequest>(() =>
    buildPaymentDataForCycle(
      cycle,
      {
        userEmail: user.email ?? "",
        channels: initialPlanPrices.registerCamera,
        storage: 1,
        isUpgrade,
      },
      DEFAULT_TERM_YEARS,
      { channelPrice: 0, storagePrice: 0 }
    )
  );
  const [page, setPage] = useState<number>(0);

  const { data: planServiceData, isLoading } = useQuery(
    ["getPlanServices", selectedAccount.accountId],
    () => getPlanServices({ accountId: selectedAccount.accountId }),
    {
      enabled: !!selectedAccount.accountId,
      retry: 0,
      onSuccess: (data: GetPlanServicesResponse) => {
        const minChannels = clampMin(data.registerCamera || 1, 1);
        setPaymentData((prev: AddPaymentMethodRequest) =>
          buildPaymentDataForCycle(
            prev.cycle,
            {
              ...toPaymentBase(prev),
              channels: hasEditedChannels
                ? clampMin(prev.channels, minChannels)
                : minChannels,
            },
            termYears,
            {
              channelPrice: data.channelPrice ?? 0,
              storagePrice: data.storagePrice ?? 0,
            }
          )
        );
      },
    }
  );

  const paymentPrice: PaymentPrice = useMemo(() => {
    if (planServiceData) {
      return {
        ...planServiceData,
        year: planServiceData.year ?? [],
        registerCamera: planServiceData.registerCamera ?? 1,
      };
    }
    return { ...initialPlanPrices };
  }, [planServiceData]);

  const mutationAddPayment = useStatusMutation(postPlanAddPayment, {
    onSuccess: (res: AddPaymentMethodResponse) => {
      const stripeUrl = res.result;
      if (!stripeUrl) return;
      window.location.replace(stripeUrl);
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const isYearly = isYearlyBillingCycle(paymentData.cycle);

  const steps = usageSignupSteps.filter(
    (step) => !(isYearly && step.key === "cloudStorage")
  );
  const lastStepIndex = steps.length - 1;

  const onChangePage = (name: string, index?: number) => {
    if (name === "next") {
      setPage((prev) => Math.min(prev + 1, lastStepIndex));
      return;
    }

    if (name === "previous") {
      setPage((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (name === "step" && index !== undefined) {
      setPage(index);
    }
  };

  const actions: UsageSignupActions = {
    onChangeBillingCycle: (cycle: BILLING_CYCLE) =>
      setPaymentData((prev: AddPaymentMethodRequest) => {
        const nextChannels = clampMin(
          prev.channels,
          paymentPrice.registerCamera
        );

        return buildPaymentDataForCycle(
          cycle,
          {
            ...toPaymentBase(prev),
            isUpgrade,
            channels: nextChannels,
          },
          termYears,
          {
            channelPrice: paymentPrice.channelPrice,
            storagePrice: paymentPrice.storagePrice,
          }
        );
      }),
    onChangeChannels: (value: number | null) =>
      setPaymentData((prev: AddPaymentMethodRequest) => {
        setHasEditedChannels(true);
        const nextChannels = clampMin(
          value ?? paymentPrice.registerCamera,
          paymentPrice.registerCamera
        );
        return {
          ...prev,
          channels: nextChannels,
        };
      }),
    onChangeStorage: (value: number | null) =>
      setPaymentData((prev: AddPaymentMethodRequest) => ({
        ...prev,
        storage: value ?? 1,
      })),
    onChangeBillingEmail: (value: string) =>
      setPaymentData((prev: AddPaymentMethodRequest) => ({
        ...buildPaymentDataForCycle(
          prev.cycle,
          { ...toPaymentBase(prev), userEmail: value },
          termYears,
          {
            channelPrice: paymentPrice.channelPrice,
            storagePrice: paymentPrice.storagePrice,
          }
        ),
      })),
    onChangeTermYears: (years: number) => {
      setTermYears(years);
      setPaymentData((prev: AddPaymentMethodRequest) =>
        buildPaymentDataForCycle(prev.cycle, toPaymentBase(prev), years, {
          channelPrice: paymentPrice.channelPrice,
          storagePrice: paymentPrice.storagePrice,
        })
      );
    },

    onChangePage,
    onAddPayment: () => {
      if (!selectedAccount.accountId) return;

      const yearlyPrices = isYearlyBillingCycle(paymentData.cycle)
        ? {
            channelPrice: getYearlyChannelPrice(
              paymentPrice.channelPrice,
              getYearDiscount(paymentPrice.year, termYears)
            ),
            storagePrice: paymentPrice.storagePrice,
          }
        : undefined;

      const payload = buildPaymentDataForCycle(
        paymentData.cycle,
        toPaymentBase(paymentData),
        termYears,
        yearlyPrices
      );

      mutationAddPayment.mutate({
        accountId: selectedAccount.accountId,
        payload,
      });
    },
  };

  const state: UsageSignupState = {
    currentStepKey: steps[page]?.key ?? "channels",
    currentStep: page,
    steps,
    isUpgrade,
    paymentData,
    paymentPrice,
    termYears,
    isLoading:
      isLoading || mutationAddPayment.loading || mutationAddPayment.isFetching,
    noPermission,
  };

  return { state, actions };
}
